/* ===========================================================================
   LUBIN — L'EMBALLAGE CADEAU (08/10/2026)

   Un produit cache, « Emballage cadeau » (3 €, handle emballage-cadeau), est
   ajoute au panier AVEC le parfum quand la case est cochee sur la fiche.
   Par article : deux flacons emballes, deux emballages.

   (1) SUR LA FICHE — case cochee, le parfum et son emballage partent en UN
       SEUL envoi a /cart/add.js, lies par un jeton :
         · le parfum porte `_emballage: <jeton>` (cache, prefixe « _ ») ;
         · l'emballage porte `Pour`, `Message` et `_lien: <jeton>`.
       « Pour » et « Message » partent dans la langue du client (16/10/2026 :
       le paiement les affiche tels quels — « Per », « Für », « Para »…).
       Case DECOCHEE : on ne fait strictement rien, le formulaire du theme
       reste seul aux commandes. Rien ne change pour un achat ordinaire.

   (2) PARTOUT — jamais plus d'emballages que de flacons (16/10/2026 : il ne
       suit plus le parfum a la hausse), et retire si le parfum l'est. Dans le tiroir, sur chaque `cart:change` ; sur la page
       panier, au chargement — le theme y RECHARGE la page a chaque
       modification, il n'y a donc pas d'evenement a ecouter.

   On rejoue exactement les evenements du theme (variant:add, cart:change,
   cart:prepare-bundled-sections) : le tiroir s'ouvre et se redessine comme
   pour n'importe quel ajout.
   =========================================================================== */
(() => {
  if (window.__lubinCadeau) return;          // le script peut etre inclus deux fois
  window.__lubinCadeau = true;

  const racine = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const reglages = () => (window.themeVariables && window.themeVariables.settings) || {};

  /* les sections a redessiner, demandees aux composants du theme */
  const sectionsAGrouper = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections',
      { bubbles: true, detail: { sections } }));
    return sections;
  };

  /* ------------------------------------------------- (2) la synchronisation */
  const corrections = (cart) => {
    const parfums = new Map();               // jeton -> quantite totale
    for (const l of cart.items || []) {
      const j = l.properties && l.properties._emballage;
      if (j) parfums.set(j, (parfums.get(j) || 0) + l.quantity);
    }
    const maj = {};
    for (const l of cart.items || []) {
      const j = l.properties && l.properties._lien;
      /* 16/10/2026 — un emballage ne s'achete jamais seul : arrive au panier
         sans jeton (lien direct, ancien panier…), il est retire. */
      const estEmballage = l.handle === 'emballage-cadeau' || l.product_type === 'Emballage';
      if (!j && !estEmballage) continue;
      /* 16/10/2026 — l'emballage ne SUIT plus le parfum a la hausse (ajouter
         un flacon n'ajoute pas d'emballage) : il est seulement plafonne au
         nombre de flacons, et retire si le parfum part. */
      const voulu = j ? Math.min(l.quantity, parfums.get(j) || 0) : 0;
      if (l.quantity !== voulu) maj[l.key] = voulu;
    }
    return maj;
  };

  let enCours = false;
  const synchronise = async (cart, surLaPagePanier) => {
    if (enCours || !cart) return;
    const maj = corrections(cart);
    if (!Object.keys(maj).length) return;    // deja coherent : on ne touche a rien
    enCours = true;
    try {
      const sections = surLaPagePanier ? [] : sectionsAGrouper();
      const r = await fetch(`${racine}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: maj, sections: sections.join(',') })
      });
      if (!r.ok) return;
      if (surLaPagePanier) { window.location.reload(); return; }
      const nouveau = await r.json();
      document.documentElement.dispatchEvent(new CustomEvent('cart:change',
        { bubbles: true, detail: { baseEvent: 'line-item:change', cart: nouveau } }));
    } finally {
      enCours = false;
    }
  };

  document.addEventListener('cart:change', (e) => {
    const cart = e.detail && e.detail.cart;
    if (cart && cart.items) synchronise(cart, false);
  });

  const auChargement = async () => {
    if (reglages().pageType !== 'cart') return;
    const cart = await (await fetch(`${racine}cart.js`)).json();
    synchronise(cart, true);
  };

  /* ------------------------------------------------------ (1) sur la fiche */
  const fiche = () => {
    const bloc = document.querySelector('[data-lpp-cadeau]');
    const form = document.getElementById('lpp-form');
    if (!bloc || !form) return;
    const pf = form.closest('product-form');
    if (!pf) return;

    const caseCochee = bloc.querySelector('[data-lpp-cadeau-case]');
    const zone = bloc.querySelector('[data-lpp-cadeau-bloc]');
    const champ = bloc.querySelector('[data-lpp-cadeau-message]');
    const compte = bloc.querySelector('[data-lpp-cadeau-compte]');
    const remetAZero = () => {
      caseCochee.checked = false; zone.hidden = true;
      champ.value = ''; compte.textContent = '0';
    };

    caseCochee.addEventListener('change', () => {
      zone.hidden = !caseCochee.checked;
      if (caseCochee.checked) champ.focus();
    });
    champ.addEventListener('input', () => { compte.textContent = String(champ.value.length); });

    /* En CAPTURE sur <product-form> : on passe avant l'ecouteur du theme, pose
       sur le formulaire lui-meme, et on l'arrete — mais SEULEMENT si la case
       est cochee. */
    pf.addEventListener('submit', async (e) => {
      if (!caseCochee.checked) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!form.checkValidity()) { form.reportValidity(); return; }

      const boutons = Array.from(form.elements).filter((b) => b.type === 'submit');
      if (boutons.some((b) => b.getAttribute('aria-busy') === 'true')) return;
      boutons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const id = Number(form.querySelector('[name="id"]').value);
      const qte = Math.max(1, parseInt(form.querySelector('[name="quantity"]').value, 10) || 1);
      const jeton = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      const choix = document.querySelector('[data-lpp-contenances] .est-choisie');
      const pour = bloc.dataset.produit + (choix ? ` — ${choix.textContent.trim()}` : '');
      const message = champ.value.trim().slice(0, 200);
      /* 16/10/2026 — les intitules partent DANS LA LANGUE DU CLIENT (« Per »,
         « Für »…) : le paiement Shopify affiche les proprietes telles quelles,
         un « Pour : » en italien etait une coquille. La boutique lit « Per :
         Akkad » sans peine ; l'affichage du panier les ramene a la langue en
         cours (snippets/line-item.liquid). */
      const motPour = bloc.dataset.motPour || 'Pour';
      const motMessage = bloc.dataset.motMessage || 'Message';
      const proprietes = { [motPour]: pour, _lien: jeton };
      if (message) proprietes[motMessage] = message;

      const sections = sectionsAGrouper();
      let r, rep;
      try {
        /* L'EMBALLAGE D'ABORD : Shopify range chaque nouvelle ligne EN TETE du
           panier. Envoye en second, l'emballage s'affichait au-dessus de son
           parfum ; envoye en premier, il se range juste dessous. */
        r = await fetch(`${racine}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [
              { id: Number(bloc.dataset.variante), quantity: qte, properties: proprietes },
              { id, quantity: qte, properties: { _emballage: jeton } }
            ],
            sections: sections.join(','),
            sections_url: `${racine}variants/${id}`
          })
        });
        rep = await r.json();
      } finally {
        boutons.forEach((b) => b.removeAttribute('aria-busy'));
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }

      if (!r.ok) {
        form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: rep && rep.description } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }
      if (reglages().cartType === 'page' || reglages().pageType === 'cart') {
        window.location.href = `${racine}cart`;
        return;
      }
      const cart = await (await fetch(`${racine}cart.js`)).json();
      cart.sections = rep.sections;
      const ajoutes = (rep.items || [rep]).slice().reverse();   // le parfum en premier, comme un ajout ordinaire
      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: {
        items: ajoutes, cart, onSuccessDo: 'force_open_drawer' } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: {
        baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart } }));
      remetAZero();      // un second ajout ne reprend pas l'emballage par megarde
    }, true);
  };

  const demarre = () => { fiche(); auChargement(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarre);
  else demarre();
})();
